import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useForm, useWatch } from 'react-hook-form';

import { fetchCustomerOrderDetail } from '#/api/crmMgmt/customerOrder/fetchDetail';
import { fetchProcessingFactoryDetail } from '#/api/momMgmt/settings/processingFactory/fetchDetail';
import { fetchProcessingFactorySelectOptions } from '#/api/momMgmt/settings/processingFactory/fetchSelectOptions';
import {
  createWorkOrderDraftBatch,
  fetchWorkOrderDraftPlan,
} from '#/api/momMgmt/workOrder';
import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import FloatingDateInput from '#/modules/commonComponents/form/FloatingDateInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import FloatingSelect from '#/modules/commonComponents/form/select';
import ManufacturingMaterialsList from '#/modules/crmMgmt/customerOrder/components/ManufacturingMaterialsList';
import ManufacturingMaterialsSourceSection from '#/modules/crmMgmt/customerOrder/components/ManufacturingMaterialsSourceSection';
import { getCustomerOrderApiErrorMessage } from '#/modules/crmMgmt/customerOrder/helper/apiError';
import {
  buildDraftBatchItemsFromRows,
} from '#/modules/crmMgmt/customerOrder/helper/buildDraftBatchItemsFromRows';
import {
  canCreateManufacturingWorkOrdersFromCustomerOrder,
  formatCustomerOrderStatusLabel,
} from '#/modules/crmMgmt/customerOrder/helper/customerOrderStatus';
import {
  findSelectedRowsMissingMaterialSelection,
} from '#/modules/crmMgmt/customerOrder/helper/manufacturingMaterialSubstitute';
import {
  resolveManufacturingMaterialsEntrySource,
} from '#/modules/crmMgmt/customerOrder/helper/manufacturingMaterialsEntrySource';
import {
  mapDraftPlanToManufacturingRows,
} from '#/modules/crmMgmt/customerOrder/helper/mapDraftPlanToManufacturingRows';
import { fetchWarehouseSelectOptions } from '#/modules/imsMgmt/settings/storePlace/helper/warehouseSelectOptions';
import PageShell from '#/modules/infra/layout/PageShell';
import {
  customerOrderDetailQueryKey,
  processingFactoryDetailQueryKey,
} from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

function getErrorMessage(error, fallback) {
  return error?.response?.data?.message
    || error?.message
    || fallback;
}

function todayIsoDate() {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

const DraftBatchSettings = ({
  control,
  register,
  errors,
  setValue,
  factoryOptions,
  warehouseOptions,
  workWarehouseOptions,
  isFactoryLoading,
  isWarehouseLoading,
  isFactoryDetailLoading,
  processingFactoryId,
}) => (
  <div className={cn('@container/form-area flex flex-col gap-4')}>
    <div className="form-block mb-0!">
      <h2 className="form-block__title">批次建單設定</h2>
      <p className="form-block__description">
        套用至勾選列。排程日同時為物料準時到料截止日。
      </p>
    </div>
    <div className="grid grid-cols-1 gap-3">
      <FloatingDateInput
        id="scheduledStartDate"
        label="排程開始日"
        control={control}
        errors={errors}
        containerClassName="mb-0!"
      />
    </div>
    <div
      className={cn(
        'mt-3 grid grid-cols-1 gap-3',
        '@md/form-area:grid-cols-3',
      )}
    >
      <FloatingSelect
        id="processingFactoryId"
        label="加工廠"
        options={factoryOptions}
        control={control}
        errors={errors}
        isClearable
        isDisabled={isFactoryLoading}
        hideErrorMessage
        containerClassName="mb-0!"
        noOptionsMessage="無可用加工廠"
        onChange={() => {
          setValue('workWarehouseId', '', { shouldDirty: true, shouldValidate: false });
        }}
      />
      <FloatingSelect
        id="workWarehouseId"
        label="工作倉"
        options={workWarehouseOptions}
        control={control}
        errors={errors}
        isClearable
        isDisabled={
          isWarehouseLoading
          || !processingFactoryId
          || isFactoryDetailLoading
        }
        hideErrorMessage
        containerClassName="mb-0!"
        noOptionsMessage={
          processingFactoryId ? '此加工廠無可用工作倉' : '請先選擇加工廠'
        }
      />
      <FloatingSelect
        id="targetWarehouseId"
        label="目標倉"
        options={warehouseOptions}
        control={control}
        errors={errors}
        isClearable
        isDisabled={isWarehouseLoading}
        hideErrorMessage
        containerClassName="mb-0!"
        noOptionsMessage="無可用倉別"
      />
    </div>
    <div
      className={cn(
        'mt-3 grid grid-cols-1 gap-3',
        '@md/form-area:grid-cols-2',
      )}
    >
      <FloatingTextarea
        id="issueReason"
        label="原因"
        register={register}
        errors={errors}
        rows={2}
        containerClassName="mb-0!"
      />
      <FloatingTextarea
        id="remark"
        label="備註"
        register={register}
        errors={errors}
        rows={2}
        containerClassName="mb-0!"
      />
    </div>
  </div>
);

DraftBatchSettings.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  setValue: PropTypes.func.isRequired,
  factoryOptions: PropTypes.arrayOf(PropTypes.object),
  warehouseOptions: PropTypes.arrayOf(PropTypes.object),
  workWarehouseOptions: PropTypes.arrayOf(PropTypes.object),
  isFactoryLoading: PropTypes.bool,
  isWarehouseLoading: PropTypes.bool,
  isFactoryDetailLoading: PropTypes.bool,
  processingFactoryId: PropTypes.string,
};

const ManufacturingMaterialsPlanEditor = ({
  customerOrderId,
  customerOrderNo,
  productFilterLabel,
  initialRows,
  isPlanLoading,
  isPlanError,
  planError,
  onRetryPlan,
}) => {
  const navigate = useNavigate();
  const backTo = customerOrderId
    ? ROUTES.customerOrderDetail(customerOrderId)
    : ROUTES.customerOrder;

  const [rows, setRows] = useState(initialRows);

  const {
    control,
    register,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      scheduledStartDate: todayIsoDate(),
      processingFactoryId: '',
      workWarehouseId: '',
      targetWarehouseId: '',
      issueReason: '客訂備料',
      remark: '',
    },
  });

  const processingFactoryId = useWatch({ control, name: 'processingFactoryId' }) || '';
  const workWarehouseId = useWatch({ control, name: 'workWarehouseId' }) || '';

  const factoryQuery = useQuery({
    queryKey: ['mom', 'processing-factory', 'select-options'],
    queryFn: fetchProcessingFactorySelectOptions,
  });

  const warehouseQuery = useQuery({
    queryKey: ['ims', 'warehouse', 'select-options'],
    queryFn: fetchWarehouseSelectOptions,
  });

  const factoryDetailQuery = useQuery({
    queryKey: processingFactoryDetailQueryKey(processingFactoryId),
    queryFn: () => fetchProcessingFactoryDetail(processingFactoryId),
    enabled: Boolean(processingFactoryId),
  });

  const factoryOptions = factoryQuery.data ?? [];
  const warehouseOptions = useMemo(
    () => warehouseQuery.data ?? [],
    [warehouseQuery.data],
  );
  const allowedWarehouseIds = useMemo(() => {
    const ids = factoryDetailQuery.data?.data?.warehouseIds ?? [];
    return new Set(ids);
  }, [factoryDetailQuery.data?.data?.warehouseIds]);

  const workWarehouseOptions = useMemo(() => {
    if (!processingFactoryId || factoryDetailQuery.isLoading) {
      return [];
    }
    return warehouseOptions.filter((option) => allowedWarehouseIds.has(option.value));
  }, [
    processingFactoryId,
    warehouseOptions,
    allowedWarehouseIds,
    factoryDetailQuery.isLoading,
  ]);

  useEffect(() => {
    if (!processingFactoryId || !workWarehouseId || factoryDetailQuery.isLoading) {
      return;
    }
    if (!allowedWarehouseIds.has(workWarehouseId)) {
      setValue('workWarehouseId', '', { shouldDirty: true, shouldValidate: false });
    }
  }, [
    processingFactoryId,
    workWarehouseId,
    allowedWarehouseIds,
    factoryDetailQuery.isLoading,
    setValue,
  ]);

  const submitMutation = useMutation({
    mutationFn: async () => {
      if (!customerOrderId) {
        throw new Error('缺少客訂 id');
      }
      const values = getValues();
      const scheduledStartDate = values.scheduledStartDate;
      const nextProcessingFactoryId = values.processingFactoryId;
      const nextWorkWarehouseId = values.workWarehouseId;
      const nextTargetWarehouseId = values.targetWarehouseId;
      if (!scheduledStartDate) {
        throw new Error('請填排程開始日');
      }
      if (!nextProcessingFactoryId) {
        throw new Error('請選擇加工廠');
      }
      if (!nextWorkWarehouseId) {
        throw new Error('請選擇工作倉');
      }
      if (!nextTargetWarehouseId) {
        throw new Error('請選擇目標倉');
      }
      const items = buildDraftBatchItemsFromRows(rows, {
        scheduledStartDate,
        processingFactoryId: nextProcessingFactoryId,
        workWarehouseId: nextWorkWarehouseId,
        targetWarehouseId: nextTargetWarehouseId,
        issueReason: values.issueReason,
        remark: values.remark,
      });
      if (items.length === 0) {
        throw new Error('請至少勾選一列並填製作量');
      }
      if (items.some((item) => !(Number(item.manufacturingQty) > 0))) {
        throw new Error('勾選列的製作量須大於 0');
      }
      const missingSelections = findSelectedRowsMissingMaterialSelection(rows);
      if (missingSelections.length > 0) {
        throw new Error('請完成所有勾選列的替代料／實際料選擇');
      }
      return createWorkOrderDraftBatch({
        customerOrderId,
        items,
      });
    },
    onSuccess: (result) => {
      const workOrders = result?.data?.workOrders ?? [];
      const count = workOrders.length;
      toast.success(result?.message || `已建立 ${count} 張工單草稿`);
      if (count === 1 && workOrders[0]?.id) {
        navigate(ROUTES.workOrderDetail(workOrders[0].id));
        return;
      }
      navigate(ROUTES.workOrder);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, '批次建立工單失敗'));
    },
  });

  return (
    <>
      <div className="create_edit_page__header">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="page_title mb-0!">
            <span className="border-l-5 border-primary-400 pl-2.5">建立多階層工單</span>
          </h1>
          <Button type="button" variant="outline" asChild>
            <Link to={backTo}>返回</Link>
          </Button>
        </div>
      </div>

      <form
        className="sheet create_edit create_edit_form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          const missingSelections = findSelectedRowsMissingMaterialSelection(rows);
          if (missingSelections.length > 0) {
            toast.warning('請完成所有勾選列的替代料／實際料選擇');
            return;
          }
          submitMutation.mutate();
        }}
      >
        <div className="create_edit_form__body divide-y divide-gray-500">
          <section className="pb-6">
            <ManufacturingMaterialsSourceSection
              customerOrder={{
                id: customerOrderId,
                customerOrderNo,
              }}
              productFilterLabel={productFilterLabel}
            />
          </section>

          <section className="pb-6">
            <DraftBatchSettings
              control={control}
              register={register}
              errors={errors}
              setValue={setValue}
              factoryOptions={factoryOptions}
              warehouseOptions={warehouseOptions}
              workWarehouseOptions={workWarehouseOptions}
              isFactoryLoading={factoryQuery.isLoading}
              isWarehouseLoading={warehouseQuery.isLoading}
              isFactoryDetailLoading={factoryDetailQuery.isLoading}
              processingFactoryId={processingFactoryId}
            />
          </section>

          <section className="pb-2">
            <div className={cn('@container/form-area flex flex-col gap-4')}>
              <div className="form-block mb-0!">
                <h2 className="form-block__title">物料計劃</h2>
                <p className="form-block__description">
                  勾選要用的物料後建立工單草稿；製作量可小於缺口。
                  建立後綁定本客訂，不可改綁。
                </p>
              </div>

              {isPlanLoading ? (
                <p className="text-sm text-muted-foreground">載入草稿計劃…</p>
              ) : null}

              {isPlanError ? (
                <div className="flex flex-col gap-2">
                  <p className="text-sm text-destructive">
                    {getErrorMessage(planError, '載入草稿計劃失敗')}
                  </p>
                  <Button type="button" variant="outline" onClick={onRetryPlan}>
                    重試
                  </Button>
                </div>
              ) : null}

              {!isPlanLoading && !isPlanError ? (
                <ManufacturingMaterialsList
                  rows={rows}
                  onRowsChange={setRows}
                />
              ) : null}
            </div>
          </section>
        </div>

        <div className="create_edit_form__actions flex flex-wrap justify-end gap-2">
          <Button type="button" variant="outline" asChild>
            <Link to={backTo}>取消</Link>
          </Button>
          <Button
            type="submit"
            disabled={submitMutation.isPending || isPlanLoading || isPlanError}
          >
            {submitMutation.isPending ? '建立中…' : '建立工單'}
          </Button>
        </div>
      </form>
    </>
  );
};

ManufacturingMaterialsPlanEditor.propTypes = {
  customerOrderId: PropTypes.string,
  customerOrderNo: PropTypes.string,
  productFilterLabel: PropTypes.string,
  initialRows: PropTypes.arrayOf(PropTypes.object).isRequired,
  isPlanLoading: PropTypes.bool,
  isPlanError: PropTypes.bool,
  planError: PropTypes.any,
  onRetryPlan: PropTypes.func,
};

/**
 * 建立多階層工單：GET draft-plan → POST draft-batch。
 * `?productId=`／`?lineId=` 可篩製品。
 */
const CustomerOrderManufacturingMaterialsPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const entrySource = resolveManufacturingMaterialsEntrySource(searchParams);
  const lineId = searchParams.get('lineId');
  const productIdFromQuery = searchParams.get('productId');

  const detailQuery = useQuery({
    queryKey: customerOrderDetailQueryKey(id),
    queryFn: () => fetchCustomerOrderDetail(id),
    enabled: Boolean(id),
  });

  const detail = detailQuery.data?.data;

  const productFilter = useMemo(() => {
    if (productIdFromQuery) {
      const lines = detail?.lines ?? [];
      const matched = lines.find((line) => (
        String(line?.product?.id ?? line?.productId) === String(productIdFromQuery)
      ));
      const product = matched?.product;
      return {
        productId: productIdFromQuery,
        label: product
          ? [product.productNo, product.name].filter(Boolean).join(' ')
          : '指定製品（資料載入中或找不到產品）',
      };
    }
    if (!lineId || !detail) {
      return { productId: null, label: null };
    }
    const line = (detail.lines ?? []).find((item) => String(item?.id) === String(lineId));
    const productId = line?.product?.id ?? line?.productId ?? null;
    if (!productId) {
      return {
        productId: null,
        label: lineId ? '指定明細（找不到對應製品）' : null,
      };
    }
    return {
      productId: String(productId),
      label: [line?.product?.productNo, line?.product?.name].filter(Boolean).join(' ')
        || '指定製品',
    };
  }, [productIdFromQuery, lineId, detail]);

  const draftPlanQuery = useQuery({
    queryKey: ['mom', 'work-order', 'draft-plan', id, productFilter.productId],
    queryFn: () => fetchWorkOrderDraftPlan({
      customerOrderId: id,
      productId: productFilter.productId,
    }),
    enabled: Boolean(id)
      && canCreateManufacturingWorkOrdersFromCustomerOrder(detail?.status),
  });

  const planMapped = useMemo(() => {
    if (!draftPlanQuery.data?.data) {
      return { rows: [], noBomShortages: [] };
    }
    return mapDraftPlanToManufacturingRows(draftPlanQuery.data.data);
  }, [draftPlanQuery.data]);

  const isLoading = Boolean(id) && detailQuery.isLoading;
  const isError = Boolean(id) && detailQuery.isError;
  const canCreateManufacturingWorkOrders = (
    canCreateManufacturingWorkOrdersFromCustomerOrder(detail?.status)
  );
  const canShowForm = Boolean(id)
    && !isLoading
    && !isError
    && canCreateManufacturingWorkOrders;
  const isPlanLoading = draftPlanQuery.isLoading;
  const isPlanError = draftPlanQuery.isError;

  return (
    <PageShell variant="form">
      <div className="layout-shell__body">
        <div className="mx-auto w-full max-w-6xl">
          {isLoading ? (
            <p className="text-sm text-slate-500">載入客訂…</p>
          ) : null}

          {isError ? (
            <p className="text-sm text-destructive">
              {getCustomerOrderApiErrorMessage(detailQuery.error, '找不到客戶訂單')}
            </p>
          ) : null}

          {!id ? (
            <p className="text-sm text-destructive">缺少客訂 id</p>
          ) : null}

          {!isLoading && !isError && id && !canCreateManufacturingWorkOrders ? (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                僅已核准的客戶訂單可建立多階層工單。目前狀態為
                {' '}
                {formatCustomerOrderStatusLabel(detail?.status)}
                ，請返回詳情。
              </p>
              <Button type="button" variant="outline" asChild>
                <Link to={ROUTES.customerOrderDetail(id)}>返回詳情</Link>
              </Button>
            </div>
          ) : null}

          {canShowForm ? (
            <ManufacturingMaterialsPlanEditor
              key={[
                entrySource,
                id,
                productFilter.productId ?? 'all',
                draftPlanQuery.dataUpdatedAt ?? 0,
                detail?.customerOrderNo ?? '',
              ].join('-')}
              customerOrderId={id}
              customerOrderNo={detail?.customerOrderNo ?? ''}
              productFilterLabel={productFilter.label}
              initialRows={planMapped.rows}
              isPlanLoading={isPlanLoading}
              isPlanError={isPlanError}
              planError={draftPlanQuery.error}
              onRetryPlan={() => draftPlanQuery.refetch()}
            />
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

export default CustomerOrderManufacturingMaterialsPage;
